const round = (value, precision = 100, floor = true) => floor
    ? Math.floor(parseFloat(value) / precision) * precision
    : Math.ceil(parseFloat(value) / precision) * precision;

// constants obtained from "Pensionsmyndighetens tumregler för pensionsberäkningar.pdf"
// updated constants for 2021:
// https://www.pensionsmyndigheten.se/forsta-din-pension/om-pensionssystemet/sa-beraknas-din-pension-basbelopp-och-varderegler

const incomePensionFact = 0.16;
const premiumPensionFact = 0.025;
const heritageProfitFact = 1.043; // OBS: kolla detta!

const incomeBaseFigure = 80600; // inkomstbasbelopp
const monthlySalaryExchangeMinimum = round((incomeBaseFigure * 8.07) / 12, 100, false);
const incomeYearlyCeiling = 650442; // avgiftstak årligen
const incomeMonthlyCeiling = incomeYearlyCeiling / 12;

const generalPensionFeeFact = 0.93; // 1 - 0.07
const incomePensionManagementCostFact = 0.998; // 1 - 0.002
const premiumPensionManagementCostFact = 0.004;

const assumedYearlySalaryGrowth = 1.018;
const assumedYearlyPremiumPensionGrowth = 1.021;

// below capital insurance constants gathered from various sources, see comments

// assumed "statslåneränta": 1.5%, actual values for the last 10 years:
// 2006: 3.62, 2012: 1.52, 2018: 0.56
const assumedGovernmentBondInterest = 1.5;

// mean growth for OMX 1990-2014 was: 12.69%, see:
// https://rikatillsammans.se/stockholmsborsens-arliga-avkastning-1990-2013/
// assume yearly growth of 8%, which is roughly 50% of the mean growth
const assumedCapitalInsuranceGrowth = 1.05; //1.08;

// capital insurance tax calculation from:
// https://www.nordnet.se/kundservice/start/vara-sparformer/kapitalforsakring-.html
const capitalInsuranceTax = ((assumedGovernmentBondInterest + 1) * 0.3)/100; // 30% tax

// from "Delningstal + premiepension 2017-12-01.xls", obtained from Pensionsmyndigheten
// const paymentDenominators = {
//     '61': 20.91,
//     '62': 20.37,
//     '63': 19.82,
//     '64': 19.26,
//     '65': 18.69,
//     '66': 18.12,
//     '67': 17.54,
//     '68': 16.29,
//     '69': 15.71,
//     '70': 15.13,
//     '71': 14.54,
//     '72': 13.96,
//     '73': 13.38,
//     '74': 12.80,
//     '75': 12.22,
//     '76': 11.64,
//     '77': 11.07,
//     '78': 10.12,
//     '79': 9.58,
//     '80': 9.05,
//     '81': 8.53,
//     '82': 8.02,
//     '83': 7.53,
//     '84': 7.05,
//     '85': 6.59
// };

// assume a pay-out time of ~20 years (the expected life span left when retiring)
const paymentDenominator = 20;

const occupationalPensionPlans = {
    'akapkl': { // municipal employment, born earlier than '86
        salaryPercentage: 0.045,
        salaryCeiling: 7.5 * incomeBaseFigure,
        monthlySalaryCeiling: 7.5 * incomeBaseFigure / 12
    },
    'kapkl': { // municipal employment, born '86 or earlier
        salaryPercentage: 0.045,
        salaryCeiling: 30 * incomeBaseFigure,
        monthlySalaryCeiling: 30 * incomeBaseFigure / 12
    },
    'itp-2': { // private employment, born later than '79
        salaryPercentage: 0.045,
        salaryCeiling: 7.5 * incomeBaseFigure,
        monthlySalaryCeiling: 7.5 * incomeBaseFigure / 12
    },
    'itp-1': { // private employment, born '79 or later
        salaryPercentage: 0.045,
        salaryCeiling: null,
        monthlySalaryCeiling: null
    },
    'none': { // no occupational pension
        salaryPercentage: 0,
        salaryCeiling: null,
        monthlySalaryCeiling: null
    }
    // 'kapan-1': {
    //
    // },
    // 'kapan-2': {
    //
    // }
};

let instance = null;
class PensionCalculationService {
    constructor() {
        if (instance) {
            return instance;
        }

        instance = {
            calculateTotalIncomePension: this.calculateTotalIncomePension,
            calculateMonthlyIncomePension: this.calculateMonthlyIncomePension,

            calculateTotalOccupationalPension: this.calculateTotalOccupationalPension,
            calculateMonthlyOccupationalPension: this.calculateMonthlyOccupationalPension,

            calculateTotalPremiumPension: this.calculateTotalPremiumPension,
            calculateMonthlyPremiumPension: this.calculateMonthlyPremiumPension,

            calculateTotalMonthlyPension: this.calculateTotalMonthlyPension,
            adjustToFuturePriceLevel: this.adjustToFuturePriceLevel
        };

        return instance;
    }

    // public //

    // income pension

    calculateTotalIncomePension = (startedWorkAge, retirementAge, monthlySalary) => {
        const numOfYearsWorked = retirementAge - startedWorkAge;

        return (
            numOfYearsWorked *
            incomePensionFact *
            heritageProfitFact *
            generalPensionFeeFact *
            incomePensionManagementCostFact *
            Math.min(monthlySalary, incomeMonthlyCeiling)
        );
    }

    calculateMonthlyIncomePension = (startedWorkAge, age, retirementAge, monthlySalary) => {
        let denominator = paymentDenominator; //this.getDenominatorForAge(retirementAge);
        let totalIncomePension = this.calculateTotalIncomePension(
            startedWorkAge,
            retirementAge,
            monthlySalary
        );

        return totalIncomePension / denominator;
    }

    // occupational pension

    calculateTotalOccupationalPension = (startedWorkAge, retirementAge, monthlySalary, pensionPlanName = null) => {
        if(!pensionPlanName) {
            console.warn('No occupational pension plan name supplied!');
            return 0;
        }

        if(!(pensionPlanName in occupationalPensionPlans)) {
            console.warn(`Occupational pension plan with name ${pensionPlanName} could not be found`);
            return 0;
        }

        const numOfYearsWorked = retirementAge - startedWorkAge;
        const startAmount = 0;
        const pensionPlan = occupationalPensionPlans[pensionPlanName];
        const yearlySalary = pensionPlan.salaryCeiling
            ? Math.min(monthlySalary * 12, pensionPlan.salaryCeiling)
            : monthlySalary * 12;

        const salaryAmountSaved = yearlySalary * pensionPlan.salaryPercentage;
        const growth = assumedYearlyPremiumPensionGrowth;

        return (
            (startAmount * Math.pow(growth, numOfYearsWorked)) +
            (salaryAmountSaved * (Math.pow(growth, numOfYearsWorked) - 1) * (1 / (growth - 1)))
        );
    }

    calculateMonthlyOccupationalPension = (startedWorkAge, retirementAge, monthlySalary, pensionPlanName = null) => {
        let denominator = paymentDenominator; //this.getDenominatorForAge(retirementAge);
        let totalOccupaptional = this.calculateTotalOccupationalPension(
            startedWorkAge,
            retirementAge,
            monthlySalary,
            pensionPlanName
        );

        return totalOccupaptional / denominator / 12;
    }

    // premium pension

    calculateTotalPremiumPension = (startedWorkAge, age, retirementAge, monthlySalary) => {
        const numOfYearsWorked = retirementAge - startedWorkAge;
        const growthFact = Math.pow(
            assumedYearlyPremiumPensionGrowth - premiumPensionManagementCostFact,
            retirementAge - age
        );

        return (
            numOfYearsWorked *
            premiumPensionFact *
            heritageProfitFact *
            generalPensionFeeFact *
            growthFact *
            Math.min(monthlySalary, incomeMonthlyCeiling)
        );
    }

    calculateMonthlyPremiumPension = (startedWorkAge, age, retirementAge, monthlySalary) => {
        let denominator = paymentDenominator; //this.getDenominatorForAge(retirementAge);
        let totalPremium = this.calculateTotalPremiumPension(
            startedWorkAge,
            age,
            retirementAge,
            monthlySalary
        );

        return totalPremium / denominator;
    }

    // private savings pension

    // TODO: only half the final sum should be taxed, see:
    // https://www.nordnet.se/kundservice/start/vara-sparformer/kapitalforsakring-.html
    calculateTotalPrivateSavingsPension = (age, retirementAge, monthlySaving, startAmount = 0) => {
        const numOfYearsWorked = retirementAge - age;
        const growth = assumedCapitalInsuranceGrowth - capitalInsuranceTax;

        const amountSaved = (
            (startAmount * Math.pow(growth, numOfYearsWorked)) +
            ((monthlySaving * 12) * (Math.pow(growth, numOfYearsWorked) - 1) * (1 / (growth - 1)))
        );

        return amountSaved;
    }

    calculateMonthlyPrivateSavingsPension = (age, retirementAge, monthlySaving, startAmount = 0) => {
        let payoutMonths = paymentDenominator * 12; //this.getDenominatorForAge(retirementAge);
        let totalPrivateSavings = this.calculateTotalPrivateSavingsPension(
            age,
            retirementAge,
            monthlySaving,
            startAmount
        );

        return totalPrivateSavings / payoutMonths;
    }

    calculateMonthlySavingsNeeded = (monthlyPayoutNeeded, age, retirementAge, startAmount = 0) => {
        let denominator = paymentDenominator; //this.getDenominatorForAge(retirementAge);
        const numOfYearsWorked = retirementAge - age;
        const growth = assumedCapitalInsuranceGrowth - capitalInsuranceTax;

        // below equation is the same as in calculateTotalPrivateSavingsPension(), only solved for  monthly saving
        let monthlySavingNeeded = (
            ( ((((monthlyPayoutNeeded * 12) * denominator) - startAmount)) * (growth - 1) ) /
                ( 12 * (Math.pow(growth, numOfYearsWorked) - 1) )
        );

        return monthlySavingNeeded > 0
            ? monthlySavingNeeded
            : 0;

    }

    // do it all

    calculateTotalMonthlyPension = (startedWorkAge, age, retirementAge, monthlySalary, wantedPension, pensionPlanName, precision = 100) => {
        let incomePension = this.calculateMonthlyIncomePension(
            startedWorkAge,
            age,
            retirementAge,
            monthlySalary
        );

        incomePension = round(incomePension, precision);

        let premiumPension = this.calculateMonthlyPremiumPension(
            startedWorkAge,
            age,
            retirementAge,
            monthlySalary
        );

        premiumPension = round(premiumPension, precision);

        let occupationalPension = this.calculateMonthlyOccupationalPension(
                startedWorkAge,
                retirementAge,
                monthlySalary,
                pensionPlanName
        );

        occupationalPension = round(occupationalPension, precision);

        const totalPensionNoSavings = incomePension + premiumPension + occupationalPension;
        const monthlyPrivateSavingsPayoutNeeded = wantedPension - totalPensionNoSavings;

        let monthlySavingsNeeded = this.calculateMonthlySavingsNeeded(
            monthlyPrivateSavingsPayoutNeeded,
            age,
            retirementAge,
            0
        );

        monthlySavingsNeeded = round(monthlySavingsNeeded, 100, false);

        let privateSavingsPension = this.calculateMonthlyPrivateSavingsPension(
            age,
            retirementAge,
            monthlySavingsNeeded,
            0
        );

        privateSavingsPension = round(privateSavingsPension, precision);

        return {
            incomePension,
            premiumPension,
            occupationalPension,
            privateSavingsPension,
            monthlySavingsNeeded,
            totalPensionNoSavings,
            totalPublicPension: incomePension
                + premiumPension,
            totalPension: incomePension
                + premiumPension
                + occupationalPension
                + privateSavingsPension
        };
    }

    adjustToFuturePriceLevel = (price, years) => {
        return price * (Math.pow(assumedYearlySalaryGrowth, years) - 1);
    }

    // private //

    // getDenominatorForAge(age) {
    //     // get matching or closest possible age
    //     const closestAge = Object.keys(paymentDenominators)
    //         .map(a => parseInt(a))
    //         .reduce((prev, curr) =>
    //             Math.abs(curr - age) < Math.abs(prev - age)
    //                 ? curr
    //                 : prev
    //         );
    //
    //     return paymentDenominators[String(closestAge)];
    // }
}

export default PensionCalculationService;

export {
    incomeBaseFigure,
    incomePensionFact,
    premiumPensionFact,
    heritageProfitFact,

    incomeYearlyCeiling,
    incomeMonthlyCeiling,
    monthlySalaryExchangeMinimum,

    generalPensionFeeFact,
    incomePensionManagementCostFact,
    premiumPensionManagementCostFact,

    assumedYearlySalaryGrowth,
    assumedYearlyPremiumPensionGrowth
}
